import BAULT_ROUTER_ABI from 'abis/BaultRouter.json'
import ISLAND_SWAP_ROUTER_ABI from 'abis/island-swap-router.json'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi, Hex, PublicClient, WriteContractReturnType } from 'viem'
import { Address, encodeFunctionData } from 'viem'

import {
  DoubleSidedParams,
  NativeParams,
  SingleSidedParams,
  StakeParams,
  UnstakeParams,
  VaultStrategy,
  WithdrawParams,
} from './VaultStrategy.type'

export class IslandVaultStrategy implements VaultStrategy {
  readonly supportsZapping = true
  readonly supportsAutoCompound = true

  constructor(
    private smartIslandRouterAddress: Address,
    private wagmiPublicClient: PublicClient,
    private writeContractAsync: (args: {
      address: Address
      abi: Abi
      functionName: string
      args?: any[]
      value?: bigint
      gas?: bigint
      account: Address
    }) => Promise<WriteContractReturnType>,
    private chainId: number
  ) {}

  stakeLiquidity?(params: StakeParams): Promise<Hex> {
    throw new Error('Method not implemented.')
  }
  unstakeLiquidity?(params: UnstakeParams): Promise<Hex> {
    throw new Error('Method not implemented.')
  }
  transactionType!: TransactionType

  get routerAddress(): string {
    return this.smartIslandRouterAddress
  }

  // ========================================
  // DOUBLE-SIDED LIQUIDITY
  // ========================================
  async addLiquidityDoubleSided(params: DoubleSidedParams): Promise<Hex> {
    const {
      poolAddress,
      parsedAmountA,
      parsedAmountB,
      amount0Min,
      amount1Min,
      amountSharesMin,
      account,
      currencyA,
      currencyB,
      useAutoCompound,
      baultAddress,
      expectedShares,
    } = params

    if (useAutoCompound && baultAddress) {
      return await this.handleAutoCompoundDeposit({
        poolAddress,
        parsedAmountA,
        parsedAmountB,
        amount0Min,
        amount1Min,
        amountSharesMin,
        account,
        currencyA,
        currencyB,
        baultAddress,
        expectedShares,
      })
    } else {
      return await this.handleRegularDeposit({
        poolAddress,
        parsedAmountA,
        parsedAmountB,
        amount0Min,
        amount1Min,
        amountSharesMin,
        account,
        currencyA,
        currencyB,
      })
    }
  }

  private async handleAutoCompoundDeposit(params: {
    poolAddress: string
    parsedAmountA: any
    parsedAmountB: any
    amount0Min: string
    amount1Min: string
    amountSharesMin: string
    account: string
    currencyA: any
    currencyB: any
    baultAddress: string
    expectedShares: any
  }): Promise<Hex> {
    const {
      poolAddress,
      parsedAmountA,
      parsedAmountB,
      amount0Min,
      amount1Min,
      amountSharesMin,
      account,
      currencyA,
      currencyB,
      baultAddress,
      expectedShares,
    } = params

    const calls: Hex[] = []
    const minSharesOut = expectedShares ? (BigInt(expectedShares.quotient.toString()) * 95n) / 100n : 0n

    if (currencyA.isNative || currencyB.isNative) {
      const tokenBIsETH = currencyB.isNative
      const value = BigInt((tokenBIsETH ? parsedAmountB : parsedAmountA).quotient.toString())

      const addLiquidityCalldata = encodeFunctionData({
        abi: BAULT_ROUTER_ABI as Abi,
        functionName: 'addLiquidityNative',
        args: [
          poolAddress as Address,
          BigInt(parsedAmountA.quotient.toString()),
          BigInt(parsedAmountB.quotient.toString()),
          BigInt(amount0Min),
          BigInt(amount1Min),
          BigInt(amountSharesMin),
          this.smartIslandRouterAddress,
        ],
      })
      calls.push(addLiquidityCalldata)

      const depositBaultCalldata = encodeFunctionData({
        abi: BAULT_ROUTER_ABI as Abi,
        functionName: 'depositIntoBaultSelf',
        args: [baultAddress as Address, minSharesOut, account as Address],
      })
      calls.push(depositBaultCalldata)

      try {
        const tx = {
          address: this.smartIslandRouterAddress,
          abi: BAULT_ROUTER_ABI as Abi,
          functionName: 'multicall',
          args: [calls],
          value,
          account: account as Address,
        } as const

        const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
        const txHash = await this.writeContractAsync({
          ...tx,
          gas: calculateGasMargin(estimatedGas) as bigint,
        })

        return txHash
      } catch (error: any) {
        console.warn('Gas estimation failed for auto-compound multicall with native token:', error)
        throw error
      }
    } else {
      const addLiquidityCalldata = encodeFunctionData({
        abi: BAULT_ROUTER_ABI as Abi,
        functionName: 'addLiquidity',
        args: [
          poolAddress as Address,
          BigInt(parsedAmountA.quotient.toString()),
          BigInt(parsedAmountB.quotient.toString()),
          BigInt(amount0Min),
          BigInt(amount1Min),
          BigInt(amountSharesMin),
          this.smartIslandRouterAddress,
        ],
      })
      calls.push(addLiquidityCalldata)

      const depositBaultCalldata = encodeFunctionData({
        abi: BAULT_ROUTER_ABI as Abi,
        functionName: 'depositIntoBaultSelf',
        args: [baultAddress as Address, minSharesOut, account as Address],
      })
      calls.push(depositBaultCalldata)

      try {
        const tx = {
          address: this.smartIslandRouterAddress,
          abi: BAULT_ROUTER_ABI as Abi,
          functionName: 'multicall',
          args: [calls],
          account: account as Address,
        } as const

        const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
        const txHash = await this.writeContractAsync({
          ...tx,
          gas: calculateGasMargin(estimatedGas) as bigint,
        })

        return txHash
      } catch (error: any) {
        console.warn('Gas estimation failed for auto-compound multicall with tokens:', error)
        throw error
      }
    }
  }

  private async handleRegularDeposit(params: {
    poolAddress: string
    parsedAmountA: any
    parsedAmountB: any
    amount0Min: string
    amount1Min: string
    amountSharesMin: string
    account: string
    currencyA: any
    currencyB: any
  }): Promise<Hex> {
    const {
      poolAddress,
      parsedAmountA,
      parsedAmountB,
      amount0Min,
      amount1Min,
      amountSharesMin,
      account,
      currencyA,
      currencyB,
    } = params

    // Native token case
    if (currencyA.isNative || currencyB.isNative) {
      const tokenBIsETH = currencyB.isNative
      const value = BigInt((tokenBIsETH ? parsedAmountB : parsedAmountA).quotient.toString())

      try {
        const tx = {
          address: this.smartIslandRouterAddress,
          abi: ISLAND_SWAP_ROUTER_ABI as Abi,
          functionName: 'addLiquidityNative',
          args: [
            poolAddress as Address,
            BigInt(parsedAmountA.quotient.toString()),
            BigInt(parsedAmountB.quotient.toString()),
            BigInt(amount0Min),
            BigInt(amount1Min),
            BigInt(amountSharesMin),
            account as Address,
          ],
          value,
          account: account as Address,
        } as const

        const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
        const txHash = await this.writeContractAsync({
          ...tx,
          gas: calculateGasMargin(estimatedGas) as bigint,
        })

        return txHash
      } catch (error: any) {
        console.warn('Gas estimation failed for regular addLiquidityNative:', error)
        throw error
      }
    } else {
      try {
        const tx = {
          address: this.smartIslandRouterAddress,
          abi: ISLAND_SWAP_ROUTER_ABI as Abi,
          functionName: 'addLiquidity',
          args: [
            poolAddress as Address,
            BigInt(parsedAmountA.quotient.toString()),
            BigInt(parsedAmountB.quotient.toString()),
            BigInt(amount0Min),
            BigInt(amount1Min),
            BigInt(amountSharesMin),
            account as Address,
          ],
          account: account as Address,
        } as const

        const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
        const txHash = await this.writeContractAsync({
          ...tx,
          gas: calculateGasMargin(estimatedGas) as bigint,
        })

        return txHash
      } catch (error: any) {
        console.warn('Gas estimation failed for regular addLiquidity:', error)
        throw error
      }
    }
  }

  // ========================================
  // NATIVE TOKEN LIQUIDITY (Single-sided with native)
  // ========================================
  async addLiquidityNative(params: NativeParams): Promise<Hex> {
    throw new Error('Must be implemented by KX Router')
  }

  // ========================================
  // SINGLE-SIDED LIQUIDITY (ERC20 token)
  // ========================================
  async addLiquiditySingleSided(params: SingleSidedParams): Promise<Hex> {
    throw new Error('Must be implemented by KX Router')
  }

  // ========================================
  // WITHDRAW LIQUIDITY (ERC20 token)
  // ========================================

  async withdrawLiquidity(params: WithdrawParams): Promise<Hex> {
    const { poolAddress, amount, currencyA, currencyB, amount0Min, amount1Min, account } = params

    const isNative = currencyA.isNative || currencyB.isNative

    if (isNative) {
      const tx = {
        address: this.smartIslandRouterAddress,
        abi: ISLAND_SWAP_ROUTER_ABI as Abi,
        functionName: 'removeLiquidityNative',
        args: [
          poolAddress as Address,
          BigInt(amount.quotient.toString()),
          BigInt(amount0Min),
          BigInt(amount1Min),
          account as Address,
        ],
        account: account as Address,
      } as const

      const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
      const txHash = await this.writeContractAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } else {
      const tx = {
        address: this.smartIslandRouterAddress,
        abi: ISLAND_SWAP_ROUTER_ABI as Abi,
        functionName: 'removeLiquidity',
        args: [
          poolAddress as Address,
          BigInt(amount.quotient.toString()),
          BigInt(amount0Min),
          BigInt(amount1Min),
          account as Address,
        ],
        account: account as Address,
      } as const

      const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)
      const txHash = await this.writeContractAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    }
  }
}
